import React, { useEffect, useMemo, useRef } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { styles } from "./style";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import { RootState, useAppThunkDispatch } from "redux/store";
import { fetchReseller } from "redux/slice/resellers";
import { getResellerById } from "redux/selector"
import { getClientById } from "redux/selector"
import { fetchClient } from "redux/slice/clients";
import queryString from "query-string";
import * as Router from "react-router-dom";

const getResellerId = (props: Router.RouteComponentProps) => {
	const query = queryString.parse(props.location.search);
	let result: number | undefined;
	if (typeof query.resellerId === "string") {
		result = parseInt(query.resellerId);
	}
	return result;
}

const getClientId = (props: Router.RouteComponentProps) => {
	const query = queryString.parse(props.location.search);
	let result: number | undefined;
	if (typeof query.clientId === "string") {
		result = parseInt(query.clientId);
	}
	return result;
}

interface Props extends Router.RouteComponentProps, WithStyles<typeof styles> {
	title: string;
	header?: React.ReactNode;
	subtitle?: React.ReactNode;
	children?: React.ReactNode;
}

const Component: React.FC<Props> = ({ classes = {}, children, title, header, subtitle, location }) => {
	const dispatch = useAppThunkDispatch();

	// Get clientId and resellerId from URL parameters
	const clientId = useMemo(() => getClientId({ location } as Router.RouteComponentProps), [location.search]);
	const resellerId = useMemo(() => {
		if (clientId === undefined) {
			return getResellerId({ location } as Router.RouteComponentProps);
		}
		return undefined;
	}, [clientId, location.search]);

	// Redux selectors
	const reseller = useSelector((state: RootState) => getResellerById(state, resellerId));
	const client = useSelector((state: RootState) => getClientById(state, clientId));

	// Latch the resolved logo URL. Once we have real data (or know no fetch is
	// needed), freeze the value so it survives Redux being cleared during login.
	const resolvedLogoRef = useRef<string | undefined>(undefined);
	if (client?.reseller.logo) {
		resolvedLogoRef.current = client.reseller.logo;
	} else if (reseller?.logo) {
		resolvedLogoRef.current = reseller.logo;
	} else if (!clientId && !resellerId) {
		resolvedLogoRef.current = "https://test-cf-azure.idxhome.com/pseudopix/agentOffice/577_clientcplogo_Logo512h.png";
	}
	const logoUrl = resolvedLogoRef.current;
	const showLogo = !!logoUrl;

	useEffect(() => {
		if (clientId === undefined) {
			if (resellerId) {
				dispatch(fetchReseller({ resellerId }));
			}
		} else {
			if (clientId) {
				dispatch(fetchClient({ clientId }));
			}
		}
	}, [clientId, resellerId]);

	const headerContent = (
		<>
			{header && (
				<Mui.Typography className={classes.header} variant="h5">
					{header}
				</Mui.Typography>
			)}
			{subtitle && (
				<Mui.Typography className={classes.subtitle} variant="body2">
					{subtitle}
				</Mui.Typography>
			)}
		</>
	);

	return (
		<>
			<Helmet title={title} />
			<div className={classes.layout}>
				<>
					{showLogo && logoUrl && (
						<img className={classes.logo} src={logoUrl} alt="" />
					)}
					<div className={classes.container}>
						{headerContent}
						{children}
					</div>
				</>
			</div>
		</>
	);
};


export const AuthLayout = withStyles(Router.withRouter(Component), styles);
